import { Grid2 } from '@mui/material'
import { useEffect } from 'react'
import {
  CMBCEnhancedPhoneInput,
  RectangleRadioButton,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { PISectionValidation } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { CustomHTMLSelect } from 'src/consumer/components/helperComponents'
import PatientSearchDropdown from './PatientSearchDropdown'
import PatientAlternativeMobiles from './PatientAlternativeMobiles'
import PatientAlternativeEmails from './PatientAlternativeEmails'
import PatientAgeField from './PatientAgeField'
import {
  FormField,
  DisplayFormField,
  CheckboxFormField,
  PatientFieldHelperText,
  PatientFieldLabel,
  PatientFormGridItem,
} from './FormField'
import {
  GRID_CONFIG,
  getImportIdLabel,
} from '../utils/patientFormConfig'
import { usePatientFormI18n } from '../utils/patientFormI18n'

export const PatientBasicInfoFields = ({
  patientDetails,
  editMode = true,
  formHook,
  validationHook,
  searchHook,
  visibilityHook,
  makeAnonymousPatient,
  isForcingAnonymousPatient = false,
  importIdFieldLabel = 'Import Id',
  clinicId,
  clinicBrandId,
  patientSearchApiSignature,
  disablePatientSearch = false,
  autoFocusMobileField = false,
}) => {
  const { copy, genderOptions, relationshipOptions } = usePatientFormI18n()

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !autoFocusMobileField ||
      makeAnonymousPatient ||
      !searchHook?.inputRef?.current
    ) {
      return undefined
    }

    const focusTimer = window.setTimeout(() => {
      searchHook.inputRef.current?.focus?.()
    }, 150)

    return () => {
      window.clearTimeout(focusTimer)
    }
  }, [autoFocusMobileField, makeAnonymousPatient, searchHook?.inputRef])

  return (
    <Grid2 container spacing={2}>
      {/* Forcing Anonymous Patient Warning */}
      {isForcingAnonymousPatient && (
        <PatientFormGridItem gridConfig={{ xs: 12 }}>
          <div className="text-info">
            {copy.contactDetailsRestrictedMessage}
          </div>
        </PatientFormGridItem>
      )}

      {/* Mobile Field */}
      {visibilityHook.isFieldVisible('mobile') && !makeAnonymousPatient && (
        <PatientFormGridItem gridConfig={GRID_CONFIG}>
          <PatientFieldLabel required>{copy.mobileLabel}</PatientFieldLabel>
          <div className="d-flex flex-column" style={{ width: '100%', minWidth: 0 }}>
            <div style={{ position: 'relative', width: '100%' }}>
              <CMBCEnhancedPhoneInput
                inputProps={{ ref: searchHook.inputRef, autoFocus: autoFocusMobileField }}
                disabled={!editMode}
                onFocus={searchHook.handleMobileFocus}
                onBlur={searchHook.handleMobileBlur}
                onKeyDown={searchHook.handlePhoneInputKeyDown}
                placeholder={copy.mobilePlaceholder}
                value={patientDetails?.mobile}
                onChange={(value) => {
                  searchHook.clearLoadedOptions()
                  formHook.handleMobileChange(value)
                }}
                fullWidth
              />

              {!disablePatientSearch && (
                <PatientSearchDropdown
                  searchHook={searchHook}
                  clinicId={clinicId}
                  clinicBrandId={clinicBrandId}
                  patientSearchApiSignature={patientSearchApiSignature}
                />
              )}
            </div>
            <PatientFieldHelperText>{copy.mobileHelper}</PatientFieldHelperText>
            {validationHook.getFieldValidation('mobile').showError && (
              <PISectionValidation>
                {validationHook.getFieldValidation('mobile').errorMessage}
              </PISectionValidation>
            )}
          </div>
        </PatientFormGridItem>
      )}

      {/* Anonymous Patient Checkbox */}
      {visibilityHook.shouldShowAnonymousCheckbox && (
        <CheckboxFormField
          label={copy.anonymousPatientLabel}
          checked={!!makeAnonymousPatient}
          onChange={(e) => formHook.handleAnonymousPatientChange(e.target.checked)}
          checkboxProps={{ size: 'small' }}
          gridConfig={{ xs: 12 }}
        >
          <div style={{ color: '#64748b', fontSize: '12px' }}>
            {copy.anonymousPatientHelper}
          </div>
        </CheckboxFormField>
      )}

      {!makeAnonymousPatient && visibilityHook.isFieldVisible('alternativeMobiles') && (
        <PatientAlternativeMobiles
          alternativeMobiles={patientDetails?.altMobiles}
          onAdd={formHook.addAlternativeMobile}
          onUpdate={formHook.updateAlternativeMobile}
          onRemove={formHook.removeAlternativeMobile}
          editMode={editMode}
          validationHook={validationHook}
          label={copy.alternateMobileLabel}
          helperText={copy.alternateMobileHelper}
        />
      )}

      {/* Related Patient Relation */}
      {visibilityHook.shouldShowRelationDropdown && (
        <PatientFormGridItem gridConfig={{ xs: 12 }}>
          <div
            style={{
              padding: '12px',
              border: '1px solid rgba(103, 121, 227, 0.18)',
              borderRadius: '10px',
              background: 'rgba(103, 121, 227, 0.05)',
            }}
          >
            <PatientFieldLabel style={{ marginBottom: '8px' }}>
              {copy.relatedPatientTitlePrefix}{' '}
              <b>{patientDetails?.relatedToExistingPatient?.name}</b>{' '}
              {copy.relatedPatientTitleSuffix}
            </PatientFieldLabel>
            <PatientFieldHelperText style={{ marginTop: 0, marginBottom: '10px' }}>
              {copy.relatedPatientHelper}
            </PatientFieldHelperText>
            <CustomHTMLSelect
              value={patientDetails?.dependentRelation}
              onChange={formHook.handleRelationChange}
              getOptionLabel={(data) => data?.label}
              getOptionValue={(data) => data?.value}
              options={relationshipOptions}
              disabled={!editMode}
            />
          </div>
        </PatientFormGridItem>
      )}

      {/* Name */}
      {visibilityHook.isFieldVisible('name') && (
        <FormField
          label={copy.nameLabel}
          value={patientDetails?.name}
          onChange={formHook.handleNameChange}
          disabled={!editMode}
          validationError={validationHook.getFieldValidation('name').errorMessage}
        />
      )}

      {/* Gender */}
      {visibilityHook.isFieldVisible('gender') && (
        <PatientFormGridItem gridConfig={GRID_CONFIG}>
          <PatientFieldLabel required>{copy.genderLabel}</PatientFieldLabel>
          <div className="d-flex flex-column" style={{ width: '100%', minWidth: 0 }}>
            <RectangleRadioButton
              disabled={!editMode}
              options={genderOptions}
              selectedId={patientDetails?.gender}
              selectionFunction={formHook.handleGenderChange}
            />
            {validationHook.getFieldValidation('gender').showError && (
              <PISectionValidation>{copy.validationChooseOne}</PISectionValidation>
            )}
          </div>
        </PatientFormGridItem>
      )}

      {/* Age */}
      {visibilityHook.isFieldVisible('age') && (
        <PatientAgeField
          patientDetails={patientDetails}
          editMode={editMode}
          formHook={formHook}
          validationHook={validationHook}
        />
      )}

      {/* Email - Basic Info */}
      {visibilityHook.shouldShowEmailInBasicInfo && (
        <FormField
          label={copy.emailLabel}
          value={patientDetails?.email}
          onChange={formHook.handleEmailChange}
          disabled={!editMode}
          helperText={copy.emailHelper}
          validationError={validationHook.getFieldValidation('email').errorMessage}
        />
      )}

      {visibilityHook.shouldShowAlternativeEmailsInBasicInfo && (
        <PatientAlternativeEmails
          alternativeEmails={patientDetails?.altEmails}
          onAdd={formHook.addAlternativeEmail}
          onUpdate={formHook.updateAlternativeEmail}
          onRemove={formHook.removeAlternativeEmail}
          editMode={editMode}
          validationHook={validationHook}
          label={copy.alternateEmailLabel}
          helperText={copy.alternateEmailHelper}
        />
      )}

      {/* City */}
      {visibilityHook.isFieldVisible('city') && (
        <FormField
          label={copy.cityLabel}
          value={patientDetails?.city}
          onChange={formHook.handleCityChange}
          disabled={!editMode}
          helperText={copy.cityHelper}
        />
      )}

      {/* Address */}
      {visibilityHook.isFieldVisible('address') && (
        <FormField
          label={copy.addressLabel}
          value={patientDetails?.address}
          onChange={formHook.handleAddressChange}
          disabled={!editMode}
          gridConfig={{ xs: 12 }}
          placeholder={copy.addressPlaceholder}
          helperText={copy.addressHelper}
        />
      )}

      {/* UHID */}
      {visibilityHook.isFieldVisible('uhid') && (
        <FormField
          label="UHID"
          value={patientDetails?.uhId || ''}
          onChange={formHook.handleUhidChange}
          disabled={!editMode}
        />
      )}

      {/* Import ID */}
      {visibilityHook.isFieldVisible('importId') && (
        <FormField
          label={getImportIdLabel(importIdFieldLabel)}
          value={patientDetails?.importData?.importedFromId || ''}
          onChange={formHook.handleImportIdChange}
          disabled={!editMode}
        />
      )}

      {/* Patient Number */}
      {visibilityHook.isFieldVisible('patientNumber') && (
        <DisplayFormField
          label="Patient Number"
          value={patientDetails?.patientNumber}
        />
      )}
    </Grid2>
  )
}

export default PatientBasicInfoFields
